import { useState, useEffect, useRef, useMemo } from "react";
import { MessageSquare, Send, Plus, X, Settings, MoreVertical, Trash2, Edit, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, PlusCircle, Loader2, FolderOpen, Users, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { DEFAULT_AI_MODEL, DEFAULT_AI_PROVIDER } from "@/config/aiDefaults";
import ChatProjectManager from '@/components/ChatProjectManager';
import type { ChatProject } from '@shared/schema';

// Define types for our API responses
interface ChatProvider {
  provider: string;
  name: string;
  description: string;
  models: {
    id: string;
    name: string;
    description: string;
    maxTokens: number;
    available: boolean;
  }[];
  available: boolean;
}

interface ChatSession {
  id: number;
  userId: number;
  title: string;
  provider: string;
  model: string;
  createdAt: string;
  updatedAt: string;
}

interface ChatMessage {
  id: number;
  sessionId: number;
  role: 'system' | 'user' | 'assistant';
  content: string;
  createdAt: string;
}

export default function AIAssistant() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Sidebar state
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  
  // Session state
  const [activeSessionId, setActiveSessionId] = useState<number | null>(null);
  
  // Message state
  const [messageInput, setMessageInput] = useState('');
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
  
  // Dialog states
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [newSessionTitle, setNewSessionTitle] = useState('');
  
  // Provider and model state
  const [selectedProvider, setSelectedProvider] = useState<string | null>(
    DEFAULT_AI_PROVIDER,
  );
  const [selectedModel, setSelectedModel] = useState<string | null>(
    DEFAULT_AI_MODEL,
  );
  const [pendingChatConfig, setPendingChatConfig] = useState<{ provider: string; model: string } | null>(null);
  
  // Project management state
  const [selectedProject, setSelectedProject] = useState<ChatProject | null>(null);
  const [showProjectManager, setShowProjectManager] = useState(false);
  
  // Refs
  const endOfMessagesRef = useRef<HTMLDivElement>(null);

  // Fetch providers
  const { 
    data: providers,
    isLoading: isLoadingProviders,
    error: providersError
  } = useQuery<ChatProvider[]>({
    queryKey: ['/api/chat/providers'],
    queryFn: async () => {
      const response = await fetch('/api/chat/providers', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') ?? ''}`
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch providers (${response.status})`);
      }

      return response.json();
    }
  });

  useEffect(() => {
    if (!providers) return;
  }, [providers]);

  useEffect(() => {
    if (providersError) {
      console.error('Error fetching providers:', providersError);
    }
  }, [providersError]);

  // Fetch sessions
  const { 
    data: sessionsData,
    isLoading: isLoadingSessions,
    error: sessionsError
  } = useQuery<ChatSession[]>({
    queryKey: ['/api/smart-chat/sessions'],
    queryFn: async () => {
      const response = await fetch('/api/smart-chat/sessions', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') ?? ''}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch sessions (${response.status})`);
      }

      return response.json();
    }
  });

  useEffect(() => {
    if (sessionsError) {
      console.error('Error fetching sessions:', sessionsError);
    }
  }, [sessionsError]);

  // Fetch session data including messages
  interface SessionDataResponse {
    session: ChatSession;
    messages: ChatMessage[];
  }
  
  const { 
    data: activeSessionData, 
    isLoading: isLoadingSession,
    error: sessionError,
    refetch: refetchActiveSession
  } = useQuery({
    queryKey: ['/api/smart-chat/sessions', activeSessionId],
    enabled: !!activeSessionId,
    retry: 1,
    // Custom query function to ensure we get the session with its messages
    queryFn: async ({ queryKey }) => {
      if (!activeSessionId) return { session: {} as ChatSession, messages: [] };
      
      try {
        // Make a direct API call to get session data
        const response = await fetch(`/api/smart-chat/sessions/${activeSessionId}`, {
          credentials: "include",
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
        });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch session data: ${response.status}`);
        }
        
        const data = await response.json();
        
        // Return the data in the expected format
        return data as SessionDataResponse;
      } catch (error) {
        console.error("Error fetching session data:", error);
        throw error;
      }
    }
  });
  
  // Extract session and messages from the transformed response
  const activeSession = activeSessionData?.session;
  const activeMessages = activeSessionData?.messages;

  // Log session data when it changes
  useEffect(() => {
    if (activeSessionData) {
      // Check if we have a proper session object structure
      if (activeSessionData.session && activeSessionData.messages) {
        // Session data loaded successfully
      }
    }
  }, [activeSessionData]);

  // Create new session
  const createSessionMutation = useMutation({
    mutationFn: (newSession: { title: string, provider: string, model: string }) => {
      return apiRequest('POST', '/api/smart-chat/sessions', newSession);
    },
    onSuccess: (data: any) => {
      
      // Clear any existing data and reset states
      setIsCreatingSession(false);
      setNewSessionTitle("");
      
      // Create an initial session data structure for the cache
      const initialSessionData = {
        session: data,
        messages: []
      };
      
      // Set the query data for the new session directly in the cache
      queryClient.setQueryData(
        ['/api/smart-chat/sessions', data.id], 
        initialSessionData
      );
      
      // Update the sessions list cache immediately instead of invalidating
      const currentSessions = queryClient.getQueryData<ChatSession[]>(['/api/smart-chat/sessions']);
      if (currentSessions) {
        // Add the new session to the top of the list
        queryClient.setQueryData(
          ['/api/smart-chat/sessions'], 
          [data, ...currentSessions]
        );
      } else {
        // If we don't have sessions yet, set it as the first session
        queryClient.setQueryData(['/api/smart-chat/sessions'], [data]);
      }
      
      // Force invalidation to ensure the sidebar refreshes immediately
      queryClient.invalidateQueries({ 
        queryKey: ['/api/smart-chat/sessions'],
        refetchType: 'active' // Force an immediate refetch for active queries
      });
      
      // Set the active session ID to the new session
      setActiveSessionId(data.id);
      
      toast({
        title: "Chat session created",
        description: "Your new chat session has been created."
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error creating session",
        description: error.message || "Failed to create chat session",
        variant: "destructive"
      });
    }
  });

  const providerList = useMemo(() => providers ?? [], [providers]);
  const selectedProviderDetails = useMemo(() => {
    if (!selectedProvider) {
      return undefined;
    }
    return providerList.find((provider) => provider.provider === selectedProvider);
  }, [providerList, selectedProvider]);

  // Update session
  const updateSessionMutation = useMutation({
    mutationFn: ({ id, updates }: { id: number, updates: { title?: string, provider?: string, model?: string } }) => {
      return apiRequest('PATCH', `/api/smart-chat/sessions/${id}`, updates);
    },
    onSuccess: (data: ChatSession) => {
      // If we're currently viewing this session, update it carefully to preserve messages
      if (activeSessionId === data.id && activeSessionData) {
        // Create an updated session data object that preserves messages
        const updatedSessionData = {
          session: data, 
          messages: activeSessionData.messages || []
        };
        
        // Update the cache directly
        queryClient.setQueryData(
          ['/api/smart-chat/sessions', data.id], 
          updatedSessionData
        );
        
        // Update the sessions list cache directly
        const currentSessions = queryClient.getQueryData<ChatSession[]>(['/api/smart-chat/sessions']);
        if (currentSessions) {
          // Update the session in the sessions list
          const updatedSessions = currentSessions.map(session => 
            session.id === data.id ? data : session
          );
          
          // Update sessions list cache
          queryClient.setQueryData(['/api/smart-chat/sessions'], updatedSessions);
        } else {
          // Only invalidate if we don't have the sessions in cache
          queryClient.invalidateQueries({ 
            queryKey: ['/api/smart-chat/sessions'],
            refetchType: 'none'
          });
        }
      } else {
        // If it's not the active session, we can do a full invalidation
        queryClient.invalidateQueries({ queryKey: ['/api/smart-chat/sessions'] });
        queryClient.invalidateQueries({ queryKey: ['/api/smart-chat/sessions', data.id] });
      }
      
      toast({
        title: "Chat session updated",
        description: "Your chat session has been updated."
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error updating session",
        description: error.message || "Failed to update chat session",
        variant: "destructive"
      });
    }
  });

  // Delete session
  const deleteSessionMutation = useMutation({
    mutationFn: (id: number) => {
      return apiRequest('DELETE', `/api/smart-chat/sessions/${id}`);
    },
    onSuccess: (_, variables) => {
      // Update the sessions list directly instead of invalidating
      const currentSessions = queryClient.getQueryData<ChatSession[]>(['/api/smart-chat/sessions']);
      if (currentSessions) {
        // Remove the deleted session
        const updatedSessions = currentSessions.filter(session => session.id !== variables);
        
        // Update sessions list cache
        queryClient.setQueryData(['/api/smart-chat/sessions'], updatedSessions);
        // If there are other sessions after deletion and active session was deleted
        if (updatedSessions.length > 0 && activeSessionId === variables) {
          // Set the first (most recent) session as active
          setActiveSessionId(updatedSessions[0].id);
        } else if (activeSessionId === variables) {
          // If no sessions left, clear active session
          setActiveSessionId(null);
        }
      } else {
        // Fall back to invalidating if we don't have the sessions list
        queryClient.invalidateQueries({ queryKey: ['/api/smart-chat/sessions'] });
        if (activeSessionId === variables) {
          setActiveSessionId(null);
        }
      }
      
      toast({
        title: "Chat session deleted",
        description: "The chat session has been deleted."
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error deleting session",
        description: error.message || "Failed to delete chat session",
        variant: "destructive"
      });
    }
  });

  // Send message (non-streaming version)
  const sendMessageMutation = useMutation({
    mutationFn: ({ sessionId, content }: { sessionId: number, content: string }) => {
      return apiRequest('POST', `/api/smart-chat/sessions/${sessionId}/messages`, { content });
    },
    onSuccess: (data: { 
      userMessage: ChatMessage, 
      aiMessage: ChatMessage, // Server returns 'aiMessage', not 'assistantMessage'
      updatedSession?: ChatSession // This is returned when title is auto-generated
    }) => {
      // Reset waiting state now that we have a response
      setIsWaitingForResponse(false);
      
      if (activeSessionId) {
        // Check if we have activeSessionData with session and messages
        if (activeSessionData && activeSessionData.session && activeSessionData.messages) {
          // Filter out any temporary messages (those with negative IDs)
          // This removes the optimistically added user message (negative IDs)
          const filteredMessages = activeSessionData.messages.filter(msg => msg.id >= 0);
          
          // Build new messages from server response
          const newMessages = [data.userMessage, data.aiMessage].filter(Boolean);
          
          // Deduplicate by message ID to prevent race condition when cache
          // is updated by both refetch and mutation onSuccess
          const existingIds = new Set(filteredMessages.map((msg: any) => msg.id));
          const uniqueNewMessages = newMessages.filter((msg: any) => !existingIds.has(msg.id));
          
          // Combine and sort by createdAt to ensure correct order
          const updatedMessages = [...filteredMessages, ...uniqueNewMessages].sort((a: any, b: any) => {
            const dateA = new Date(a.createdAt).getTime();
            const dateB = new Date(b.createdAt).getTime();
            return dateA - dateB;
          });
          
          // Check if we received an updated session (e.g., if the title was auto-generated)
          const sessionToUse = data.updatedSession || activeSessionData.session;
          
          // If we have an updated session, also update the sessions list cache
          if (data.updatedSession) {
            // Get current sessions list
            const currentSessions = queryClient.getQueryData<ChatSession[]>(['/api/smart-chat/sessions']);
            
            if (currentSessions) {
              // Update the session in the sessions list
              const updatedSessions = currentSessions.map(session => 
                session.id === data.updatedSession?.id ? data.updatedSession : session
              );
              
              // Update sessions list cache
              queryClient.setQueryData(['/api/smart-chat/sessions'], updatedSessions);
              // Force invalidation to ensure the sidebar refreshes
              queryClient.invalidateQueries({ 
                queryKey: ['/api/smart-chat/sessions'],
                refetchType: 'active' // Force an immediate refetch for active queries
              });
            }
          }
          
          // Create an updated session data object
          const updatedSessionData = {
            session: sessionToUse,
            messages: updatedMessages
          };
          
          // Use setQueryData to update the cache directly
          // This will update the UI without making a new request
          queryClient.setQueryData(
            ['/api/smart-chat/sessions', activeSessionId], 
            updatedSessionData
          );
          
          // Only invalidate the sessions list if we didn't already update it above
          if (!data.updatedSession) {
            queryClient.invalidateQueries({ 
              queryKey: ['/api/smart-chat/sessions'],
              refetchType: 'none' // Don't immediately refetch
            });
          }
          
        } else {
          console.error("Cannot update session data - missing active session data structure");
          // If we don't have local session data, we need to fetch it
          queryClient.refetchQueries({ queryKey: ['/api/smart-chat/sessions', activeSessionId] });
        }
      }
    },
    onError: (error: any) => {
      console.error("Error sending message:", error);
      
      // Reset waiting state on error too
      setIsWaitingForResponse(false);
      
      // Check if the error is due to missing API key
      const errorMessage = error.response?.data?.suggestion || error.message || "Failed to send message";
      
      toast({
        title: "Error sending message",
        description: errorMessage,
        variant: "destructive"
      });
      
      // If we have active session data, remove any temporary messages
      if (activeSessionId && activeSessionData && activeSessionData.session && activeSessionData.messages) {
        // Filter out any temporary messages (those with negative IDs)
        const filteredMessages = activeSessionData.messages.filter(msg => msg.id >= 0);
        
        // Only update if we actually removed something
        if (filteredMessages.length < activeSessionData.messages.length) {
          // Create an updated session data object without the temporary message
          const updatedSessionData = {
            session: activeSessionData.session,
            messages: filteredMessages
          };
          
          // Update the cache to remove the temporary message
          queryClient.setQueryData(
            ['/api/smart-chat/sessions', activeSessionId], 
            updatedSessionData
          );
        }
      }
    }
  });

  // Simple function to send a message and simulate a streaming response
  const sendStreamingMessage = async (sessionId: number, content: string) => {
    try {
      // Set waiting state
      setIsWaitingForResponse(true);
      
      // Create a temporary user message to show immediately
      const tempUserMessage = {
        id: -999,
        sessionId: sessionId,
        role: 'user',
        content: content,
        createdAt: new Date().toISOString()
      };
      
      // Show user message immediately - handle both cases:
      // 1. If we already have session data in the cache
      if (activeSessionData) {
        const newMessages = [...activeSessionData.messages, tempUserMessage];
        queryClient.setQueryData(
          ['/api/smart-chat/sessions', sessionId],
          { ...activeSessionData, messages: newMessages }
        );
      } 
      // 2. If this is a brand new session and we don't have data yet
      else {
        // Get the session from the sessions list cache
        const currentSessions = queryClient.getQueryData<ChatSession[]>(['/api/smart-chat/sessions']) || [];
        const sessionInfo = currentSessions.find((s: ChatSession) => s.id === sessionId);
        
        if (sessionInfo) {
          // Create an initial session data structure
          const initialSessionData = {
            session: sessionInfo,
            messages: [tempUserMessage]
          };
          
          // Add it to the cache
          queryClient.setQueryData(
            ['/api/smart-chat/sessions', sessionId],
            initialSessionData
          );
          
        }
      }
      
      // Scroll to bottom regardless
      if (endOfMessagesRef.current) {
        endOfMessagesRef.current.scrollIntoView({ behavior: 'smooth' });
      }
      
      // Send the actual message to server
      const response = await fetch(`/api/smart-chat/sessions/${sessionId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content }),
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to send message (${response.status})`);
      }
      
      // Get response with real messages
      const data = await response.json();
      
      // Get current messages from cache
      const currentData = queryClient.getQueryData(['/api/smart-chat/sessions', sessionId]) as any;
      
      if (currentData) {
        // Replace temp message with real one
        const messagesWithoutTemp = currentData.messages.filter((msg: any) => msg.id !== -999);
        const messagesWithUser = [...messagesWithoutTemp, data.userMessage];
        
        // Update cache with real user message
        queryClient.setQueryData(
          ['/api/smart-chat/sessions', sessionId],
          { ...currentData, messages: messagesWithUser }
        );
        
        // Create empty AI message for streaming
        const emptyAIMessage = {
          ...data.assistantMessage,
          content: ''
        };
        
        // Add empty AI message
        const messagesWithEmptyAI = [...messagesWithUser, emptyAIMessage];
        queryClient.setQueryData(
          ['/api/smart-chat/sessions', sessionId],
          { ...currentData, messages: messagesWithEmptyAI }
        );
        
        // Simulate streaming the AI response
        const fullText = data.assistantMessage.content;
        let currentText = '';
        
        const streamInterval = setInterval(() => {
          // Get latest cache
          const latestData = queryClient.getQueryData(['/api/smart-chat/sessions', sessionId]) as any;
          
          if (!latestData) {
            clearInterval(streamInterval);
            return;
          }
          
          // If we've streamed the full message
          if (currentText.length >= fullText.length) {
            clearInterval(streamInterval);
            
            // Update with final AI message
            const finalMessages = latestData.messages.map((msg: any) => {
              if (msg.id === emptyAIMessage.id) {
                return data.assistantMessage; // Use the real message
              }
              return msg;
            });
            
            // Check if we received an updated session (e.g., auto-generated title)
            const sessionToUse = data.updatedSession || latestData.session;
            
            // Update the session cache
            queryClient.setQueryData(
              ['/api/smart-chat/sessions', sessionId],
              { session: sessionToUse, messages: finalMessages }
            );
            
            // If we have an updated session, also update the sessions list cache
            if (data.updatedSession) {
              // Get current sessions list
              const currentSessions = queryClient.getQueryData<ChatSession[]>(['/api/smart-chat/sessions']);
              
              if (currentSessions) {
                // Update the session in the sessions list
                const updatedSessions = currentSessions.map(session => 
                  session.id === data.updatedSession?.id ? data.updatedSession : session
                );
                
                // Update sessions list cache
                queryClient.setQueryData(['/api/smart-chat/sessions'], updatedSessions);
              }
            } else {
              // Only invalidate sessions list if we didn't already update it
              queryClient.invalidateQueries({ 
                queryKey: ['/api/smart-chat/sessions'],
                refetchType: 'none' 
              });
            }
            
            // Reset state
            setIsWaitingForResponse(false);
            return;
          }
          
          // Add a few characters at a time
          const chunkSize = Math.floor(Math.random() * 5) + 1;
          const nextEnd = Math.min(currentText.length + chunkSize, fullText.length);
          currentText = fullText.substring(0, nextEnd);
          
          // Update streaming message
          const updatedMessages = latestData.messages.map((msg: any) => {
            if (msg.id === emptyAIMessage.id) {
              return { ...msg, content: currentText };
            }
            return msg;
          });
          
          queryClient.setQueryData(
            ['/api/smart-chat/sessions', sessionId],
            { ...latestData, messages: updatedMessages }
          );
          
          // Scroll to bottom
          if (endOfMessagesRef.current) {
            endOfMessagesRef.current.scrollIntoView({ behavior: 'smooth' });
          }
        }, 30); // Update every 30ms
      }
    } catch (error: any) {
      console.error('Error sending message:', error);
      
      // Reset state
      setIsWaitingForResponse(false);
      
      toast({
        title: 'Error',
        description: error.message || 'Failed to send message',
        variant: 'destructive'
      });
    }
  };

  // Scroll to bottom when messages update
  useEffect(() => {
    if (endOfMessagesRef.current) {
      endOfMessagesRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [activeMessages]);

  // Handle Unsplash image clicks
  useEffect(() => {
    const handleUnsplashImageClick = (event: Event) => {
      const target = event.target as HTMLElement;
      const imageItem = target.closest('.unsplash-image-item') as HTMLElement;
      
      if (imageItem) {
        const downloadUrl = imageItem.getAttribute('data-download-url');
        if (downloadUrl) {
          // Open the Unsplash page in a new tab where users can view and download the image
          window.open(downloadUrl, '_blank', 'noopener,noreferrer');
        }
      }
    };

    // Add click event listeners to all Unsplash images
    const addImageClickListeners = () => {
      const unsplashImages = document.querySelectorAll('.unsplash-image-item');
      unsplashImages.forEach(image => {
        image.removeEventListener('click', handleUnsplashImageClick); // Remove existing listener
        image.addEventListener('click', handleUnsplashImageClick);
      });
    };

    // Add listeners when messages change (after new content is rendered)
    const timeout = setTimeout(addImageClickListeners, 100);

    return () => {
      clearTimeout(timeout);
      // Clean up event listeners
      const unsplashImages = document.querySelectorAll('.unsplash-image-item');
      unsplashImages.forEach(image => {
        image.removeEventListener('click', handleUnsplashImageClick);
      });
    };
  }, [activeMessages]);

  // Select first session when sessions are loaded if none is selected
  // - BUT only when we haven't explicitly set it to null to create a new chat
  useEffect(() => {
    // Only auto-select a session if:
    // 1. sessions is loaded and has at least one session
    // 2. activeSessionId is undefined (not initialized yet) - not null (explicitly cleared)
    // 3. We're not in the middle of creating a new session (no pendingChatConfig)
    const shouldAutoSelectFirstSession = 
      sessionsData && 
      Array.isArray(sessionsData) && 
      sessionsData.length > 0 && 
      activeSessionId === undefined && // Only when it's undefined, not null
      !pendingChatConfig;
    
    if (shouldAutoSelectFirstSession) {
      setActiveSessionId(sessionsData[0].id);
    }
  }, [sessionsData, activeSessionId, pendingChatConfig]);
  
  // Handler for storing chat configuration (no session creation yet)
  const handleSelectModel = (useDialog = true) => {
    // If we're not using the dialog, use the currently selected provider/model
    if (!useDialog) {
      // Make sure we have a provider and model selected
      if (!selectedProvider || !selectedModel) {
        toast({
          title: "Selection required",
          description: "Please select an AI provider and model",
          variant: "destructive"
        });
        return;
      }
      
      // Store the selected provider and model in the pending chat configuration
      setPendingChatConfig({
        provider: selectedProvider,
        model: selectedModel
      });
      
      // Display a toast confirming model selection
      const availableModels = getAvailableModels();
      const modelInfo = availableModels.find((model) => model.id === selectedModel);
      const modelName = modelInfo?.name || selectedModel;
      
      toast({
        title: "Model selected",
        description: `${modelName} is ready. Type your message to begin.`
      });
      
      // Close the dialog if it's open
      setIsCreatingSession(false);
      return;
    }
    
    // Dialog-based selection
    if (!selectedProvider || !selectedModel) {
      toast({
        title: "Selection required",
        description: "Please select an AI provider and model",
        variant: "destructive"
      });
      return;
    }

    // Store selected provider and model
    setPendingChatConfig({
      provider: selectedProvider,
      model: selectedModel
    });
    
    // Display confirmation
    const availableModels = getAvailableModels();
    const modelInfo = availableModels.find((model) => model.id === selectedModel);
    const modelName = modelInfo?.name || selectedModel;
    
    toast({
      title: "Model selected",
      description: `${modelName} is ready. Type your message to begin.`
    });
    
    // Close the creation dialog
    setIsCreatingSession(false);
  };

  // Handler for sending a message
  const handleSendMessage = async () => {
    // Check if we have a message to send
    if (!messageInput.trim()) return;
    
    // Store the message content before clearing the input
    const messageContent = messageInput;
    
    // Clear the input immediately for better UX
    setMessageInput("");
    
    // If we have an active session, send the message to that session
    if (activeSessionId) {
      sendStreamingMessage(activeSessionId, messageContent);
      return;
    }
    
    // If we don't have an active session but have pendingChatConfig,
    // create a new session using the pending settings and then send the message
    if (pendingChatConfig) {
      setIsWaitingForResponse(true);
      
      // Create a temporary user message to show immediately
      const tempUserMessage = {
        id: -999,
        sessionId: -1, // Temporary ID that will be updated
        role: 'user',
        content: messageContent,
        createdAt: new Date().toISOString()
      };
      
      try {
        // Create a new session with the pending config
        const sessionResponse = await apiRequest('POST', '/api/smart-chat/sessions', {
          title: `Chat with ${pendingChatConfig.provider.toLowerCase()}`,
          provider: pendingChatConfig.provider,
          model: pendingChatConfig.model
        });
        
        // Get the session ID from the response
        const newSessionId = sessionResponse.id;
        
        // Set the active session ID
        setActiveSessionId(newSessionId);
        
        // Create an initial session data structure for the cache with the user message already added
        const initialSessionData = {
          session: sessionResponse,
          messages: [tempUserMessage]
        };
        
        // Set the query data for the new session directly in the cache
        queryClient.setQueryData(
          ['/api/smart-chat/sessions', newSessionId], 
          initialSessionData
        );
        
        // Update the sessions list cache immediately
        const currentSessions = queryClient.getQueryData<ChatSession[]>(['/api/smart-chat/sessions']);
        if (currentSessions) {
          queryClient.setQueryData(
            ['/api/smart-chat/sessions'], 
            [sessionResponse, ...currentSessions]
          );
        } else {
          queryClient.setQueryData(['/api/smart-chat/sessions'], [sessionResponse]);
        }
        
        // Force invalidation to ensure the sidebar refreshes immediately
        queryClient.invalidateQueries({ 
          queryKey: ['/api/smart-chat/sessions'],
          refetchType: 'active' // Force an immediate refetch for active queries
        });
        
        // Scroll to bottom to see the user message
        if (endOfMessagesRef.current) {
          endOfMessagesRef.current.scrollIntoView({ behavior: 'smooth' });
        }
        
        // Now send the message to the newly created session
        sendStreamingMessage(newSessionId, messageContent);
      } catch (error: any) {
        console.error("Error creating session:", error);
        setIsWaitingForResponse(false);
        
        toast({
          title: "Error creating chat",
          description: error.message || "Failed to create chat session",
          variant: "destructive"
        });
      }
    } else {
      // No pending chat config and no active session
      toast({
        title: "No model selected",
        description: "Please select an AI model before sending a message",
        variant: "destructive"
      });
    }
  };

  // Initialize provider and model based on available providers
  useEffect(() => {
    if (!providerList.length) {
      return;
    }

    const defaultProviderEntry = providerList.find(
      (provider) => provider.provider === DEFAULT_AI_PROVIDER,
    );

    if (defaultProviderEntry?.available) {
      setSelectedProvider(DEFAULT_AI_PROVIDER);

      const defaultModelEntry = defaultProviderEntry.models?.find(
        (model: ChatProvider["models"][number]) =>
          model.id === DEFAULT_AI_MODEL,
      );
      if (defaultModelEntry?.available) {
        setSelectedModel(defaultModelEntry.id);
        return;
      }

      const fallbackDefaultModel =
        defaultProviderEntry.models?.find(
          (model: ChatProvider["models"][number]) => model.available,
        ) ?? defaultProviderEntry.models?.[0];
      if (fallbackDefaultModel) {
        setSelectedModel(fallbackDefaultModel.id);
        return;
      }
    }

    const firstAvailableProvider = providerList.find((provider) => provider.available);
    if (firstAvailableProvider) {
      setSelectedProvider(firstAvailableProvider.provider);
      const firstModel =
        firstAvailableProvider.models?.find(
          (model: ChatProvider["models"][number]) => model.available
        ) ?? firstAvailableProvider.models?.[0];
      if (firstModel) {
        setSelectedModel(firstModel.id);
      }
    }
  }, [providerList]);
  
  // Automatically set pendingChatConfig when selectedProvider or selectedModel changes
  useEffect(() => {
    // Only set pendingChatConfig if both provider and model are selected
    if (selectedProvider && selectedModel) {
      setPendingChatConfig({
        provider: selectedProvider,
        model: selectedModel
      });
    }
  }, [selectedProvider, selectedModel]);

  // Get available models for selected provider
  const getAvailableModels = (): ChatProvider["models"] => {
    if (!selectedProviderDetails) {
      return [];
    }

    return selectedProviderDetails.models ?? [];
  };

  function splitByUnsplashGridNodes(content: string): (JSX.Element | string)[] {
    const nodes: (JSX.Element | string)[] = [];
    let cursor = 0;
    while (true) {
      const markerIdx = content.indexOf('unsplash-image-grid', cursor);
      if (markerIdx === -1) {
        const tail = content.slice(cursor);
        if (tail) nodes.push(...formatMessageContent(tail));
        break;
      }
      const startDiv = content.lastIndexOf('<div', markerIdx);
      if (startDiv === -1) {
        const tail = content.slice(cursor);
        if (tail) nodes.push(...formatMessageContent(tail));
        break;
      }
      // Add formatted text before grid
      const before = content.slice(cursor, startDiv);
      if (before) nodes.push(...formatMessageContent(before));

      // Find matching end of the grid block by counting divs
      const re = /<\/?div\b/gi;
      re.lastIndex = startDiv;
      let depth = 0;
      let endIdx = -1;
      let m: RegExpExecArray | null;
      while ((m = re.exec(content)) !== null) {
        if (content[m.index + 1] === '/') depth--; else depth++;
        if (depth === 0 && m.index > startDiv) { endIdx = re.lastIndex; break; }
      }
      if (endIdx === -1) {
        // If we cannot find the end, fall back to formatting remaining text
        const rest = content.slice(startDiv);
        if (rest) nodes.push(...formatMessageContent(rest));
        break;
      }
      const gridHtml = content.slice(startDiv, endIdx);
      nodes.push(<div key={`unsplash-grid-${Math.random()}`} className="my-4" dangerouslySetInnerHTML={{ __html: gridHtml }} />);
      cursor = endIdx;
    }
    return nodes;
  }

  // Format message content with markdown; render Unsplash grid HTML blocks as raw HTML
  const formatMessageContent = (content: string): (JSX.Element | string)[] => {
    // Short-circuit to split and render any Unsplash grid blocks first
    if (content.includes('unsplash-image-grid')) {
      return splitByUnsplashGridNodes(content);
    }

    // Process the content line by line with markdown formatting
    return content.split('\n').map((line, i) => {
      let formattedLine = line;
      
      // Handle bold text with ** first to avoid conflicts
      formattedLine = formattedLine.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      
      // Handle italic text with single * - but only if:
      // 1. Not at start of line (bullet points)
      // 2. Has non-whitespace content between asterisks
      // 3. Asterisks are not part of an already processed ** pattern
      if (!line.match(/^\s*\*/)) { // Skip lines that start with asterisk (bullet points)
        formattedLine = formattedLine.replace(/\*([^\s*][^*]*?)\*/g, '<em>$1</em>');
      }
      
      // Handle headers
      if (line.startsWith('# ')) {
        return <h1 key={i} className="text-xl font-bold mt-3 mb-2">{line.substring(2)}</h1>;
      } else if (line.startsWith('## ')) {
        return <h2 key={i} className="text-lg font-bold mt-3 mb-2">{line.substring(3)}</h2>;
      } else if (line.startsWith('### ')) {
        return <h3 key={i} className="text-md font-bold mt-2 mb-1">{line.substring(4)}</h3>;
      }
      
      // Handle bullet points - support for both * and - list formats
      else if (line.match(/^\s*[\*\-]\s/)) {
        return <div key={i} className="flex ml-4 mt-1">
          <div className="mr-2">•</div>
          <div className="flex-1" dangerouslySetInnerHTML={{ 
            __html: formattedLine.replace(/^\s*[\*\-]\s/, '') 
          }} />
        </div>;
      }
      
      // Handle numbered lists (1. 2. etc)
      else if (line.match(/^\s*\d+\.\s/)) {
        const number = line.match(/^\s*(\d+)\.\s/)?.[1] || "";
        return <div key={i} className="flex ml-4 mt-1">
          <div className="mr-2 min-w-[20px] text-blue-500 font-bold">{number}.</div>
          <div className="flex-1" dangerouslySetInnerHTML={{ 
            __html: formattedLine.replace(/^\s*\d+\.\s/, '') 
          }} />
        </div>;
      }
      
      // Handle empty lines as paragraph breaks
      else if (line.trim() === '') {
        return <div key={i} className="h-2"></div>; // Add some spacing
      }
      
      // Linkify emails and URLs
      formattedLine = formattedLine.replace(/\b([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})\b/g, '<a href="mailto:$1" class="text-orange-600 hover:underline">$1</a>');
      formattedLine = formattedLine.replace(/(?<!\]\(|\))(https?:\/\/[^\s<>]+)(?![^\(]*\))/g, '<a href="$1" class="text-orange-600 hover:underline" target="_blank" rel="noopener noreferrer">$1</a>');

      // Format US phone numbers
      formattedLine = formattedLine.replace(/(?<!\w)(?:\+1[\s.-]?)?(?:\(\d{3}\)|\d{3})[\s.-]?\d{3}[\s.-]?\d{4}(?!\w)/g, (m) => {
        const digits = (m.match(/\d/g) || []).join('');
        let d = digits;
        if (d.length === 11 && d.startsWith('1')) d = d.slice(1);
        if (d.length !== 10) return m;
        const formatted = `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
        const tel = digits.startsWith('1') ? digits : `1${d}`;
        return `<a href="tel:${tel}" class="text-orange-600 hover:underline">${formatted}</a>`;
      });

      // Regular lines with inline formatting
      return <div key={i} dangerouslySetInnerHTML={{ __html: formattedLine }} />;
    });
  };

  const UnsplashGrid: React.FC<{ meta: any }> = ({ meta }) => {
    const images = meta?.unsplash?.images || [];
    const [open, setOpen] = useState(false);
    const [index, setIndex] = useState(0);
    if (!images || images.length === 0) return null;

    const openAt = (i: number) => { setIndex(i); setOpen(true); };
    const close = () => setOpen(false);
    const next = () => setIndex((i) => Math.min(i + 1, images.length - 1));
    const prev = () => setIndex((i) => Math.max(i - 1, 0));
    const current = images[index] || {};

    useEffect(() => {
      if (!open) return;
      const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') { e.preventDefault(); close(); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); }
        if (e.key === 'ArrowRight') { e.preventDefault(); next(); }
      };
      window.addEventListener('keydown', onKey);
      return () => window.removeEventListener('keydown', onKey);
    }, [open, index]);

    const downloadImage = () => {
      try {
        const url = current.full_url || current.download_url || current.thumbnail_url;
        if (!url) return;

        const a = document.createElement('a');
        a.href = url;
        const urlObj = new URL(url);
        let base = urlObj.pathname.split('/').pop() || (current.id || 'image');
        if (!/\.(jpg|jpeg|png|webp)$/i.test(base)) base += '.jpg';
        a.download = base;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        document.body.appendChild(a);
        a.click();
        a.remove();
      } catch (e) { console.error('Download failed:', e); }
    };

    // Copy button removed

    return (
      <div className="mt-3">
        <div className="text-xs text-gray-600 mb-1">
          Visual Inspiration · {meta.unsplash.count} images for "{meta.unsplash.searchTerms}"
        </div>
        <div className="unsplash-image-grid">
          {images.map((img: any, i: number) => (
            <div key={img.id} className="unsplash-image-item" onClick={() => openAt(i)} title={img.description}>
              <img src={img.thumbnail_url} alt={img.description} className="unsplash-thumbnail" />
              <div className="unsplash-attribution">
                <a href={img.photographer.profile_url} target="_blank" rel="noopener noreferrer" onClick={(e)=>e.stopPropagation()}>{img.photographer.name}</a> | <a href="https://unsplash.com" target="_blank" rel="noopener noreferrer" onClick={(e)=>e.stopPropagation()}>Unsplash</a>
              </div>
              {/* meta hidden by CSS */}
            </div>
          ))}
        </div>

        {open && (
          <div className="fixed inset-0 z-[1000] bg-black/70 flex items-center justify-center" onClick={close}>
            <div
              className="relative max-w-[90vw] p-2 bg-white rounded-lg"
              style={{ minHeight: '300px', maxHeight: '80vh', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
              onClick={(e) => e.stopPropagation()}
            >
            <button
              onClick={close}
              className="absolute top-3 right-3 h-8 w-8 rounded-full bg-white/90 text-gray-800 hover:bg-white shadow-lg border border-gray-200 flex items-center justify-center transition-colors"
              title="Close"
            >
              ✕
            </button>
              <div className="flex-1 w-full flex items-center justify-center overflow-hidden">
                <img src={current.full_url} alt={current.description} style={{ maxWidth: '85vw', maxHeight: '100%', height: 'auto', width: 'auto', objectFit: 'contain' }} />
              </div>
              <div className="flex items-center justify-between w-full px-2 py-2">
                <div className="text-xs text-gray-600">{index + 1} / {images.length}</div>
                <div className="flex items-center gap-2">
                  <button onClick={prev} disabled={index === 0} className="p-2 bg-gray-100 rounded disabled:opacity-50" title="Previous">
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button onClick={next} disabled={index === images.length - 1} className="p-2 bg-gray-100 rounded disabled:opacity-50" title="Next">
                    <ChevronRight className="w-4 h-4" />
                  </button>
                  <button onClick={downloadImage} className="p-2 bg-gray-100 rounded" title="Download">
                    <Download className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Fun loading messages for the oracle effect
  const LOADING_MESSAGES = [
    "Entering the matrix...",
    "Opening hailing frequencies...",
    "Accessing the mainframe like Neo...",
    "Searching the galaxy far, far away...",
    "These aren't the droids you're looking for... wait, yes they are...",
    "Running it through the AI magic 8-ball...",
    "Firing up the neural networks...",
    "Spinning up the hamster wheel...",
    "Diving into the data stream...",
    "Summoning the power of 1s and 0s...",
    "Consulting the digital oracle...",
    "Calibrating the flux capacitor...",
    "Searching space time for The Doctor...",
    "Running diagnostics on the TARDIS...",
    "Parsing the Prime Directive for loopholes...",
    "Prepping for the Kessel Run...",
    "Defragmenting my thoughts...",
    "Channeling Lewis Skolnick...",
    "Doing the truffle shuffle with data packets...",
    "Accessing WOPR database...",
    "Joshua is thinking... please stand by...",
    "The only winning move is not to play... just kidding, here's your answer...",
    "Would you like to play a game? No? Okay, here's your answer...",
    "Phoning home...",
    "Johnny 5 is alive... and processing your request!"
  ];
  const getRandomLoadingMessage = () => {
    return LOADING_MESSAGES[Math.floor(Math.random() * LOADING_MESSAGES.length)];
  };

  return (
    <div className="flex h-full overflow-hidden">
      {/* Chat sessions sidebar */}
      <div className={`border-r relative ${isSidebarCollapsed ? 'w-14' : 'w-80'} transition-all bg-gray-100/40 dark:bg-gray-900/40 flex flex-col h-full`}>
        <div className={`p-4 border-b flex items-center ${isSidebarCollapsed ? 'justify-center' : 'justify-between'}`}>
          {isSidebarCollapsed ? (
            <MessageSquare className="h-5 w-5" />
          ) : (
            <div className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              <h2 className="font-semibold">Chat Sessions</h2>
            </div>
          )}
          
          {!isSidebarCollapsed && (
            <Button 
              variant="ghost" 
              size="icon"
              className="h-8 w-8" 
              title="New Chat"
              onClick={() => {
                // Force a refresh with these explicit state updates
                setActiveSessionId(null);
                
                // Set the default provider and model
                setSelectedProvider(DEFAULT_AI_PROVIDER);
                setSelectedModel(DEFAULT_AI_MODEL);

                // Set pending chat config explicitly with default values
                setPendingChatConfig({
                  provider: DEFAULT_AI_PROVIDER,
                  model: DEFAULT_AI_MODEL,
                });
                
              }}
            >
              <PlusCircle className="h-4 w-4 text-blue-500" />
            </Button>
          )}
          
          {/* Collapse/Expand button - positioned absolutely like in MainLayout */}
          {!isSidebarCollapsed && (
            <Button 
              variant="ghost" 
              size="sm"
              className="absolute -right-3 top-4 h-6 w-6 p-0.5 bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-full shadow-sm"
              onClick={() => setIsSidebarCollapsed(true)}
            >
              <ChevronLeft className="h-3.5 w-3.5 text-gray-500" />
            </Button>
          )}
          {isSidebarCollapsed && (
            <Button 
              variant="ghost" 
              size="sm"
              className="absolute -right-3 top-4 h-6 w-6 p-0.5 bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-full shadow-sm"
              onClick={() => setIsSidebarCollapsed(false)}
            >
              <ChevronRight className="h-3.5 w-3.5 text-gray-500" />
            </Button>
          )}
        </div>
        
        {/* Sessions list - different view based on expanded or collapsed state */}
        {!isSidebarCollapsed && (
          <div className="flex-1 overflow-y-auto">
            {/* Project Management Section */}
            <div className="border-b bg-gray-50/50 dark:bg-gray-950/50">
              <div className="p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FolderOpen className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Current Project</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowProjectManager(!showProjectManager)}
                    className="h-6 w-6 p-0"
                  >
                    <Settings className="h-3 w-3" />
                  </Button>
                </div>
                
                {selectedProject ? (
                  <div className="bg-white dark:bg-gray-800 rounded-md p-2 border">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: selectedProject?.color ?? undefined }}
                      />
                      <span className="text-sm font-medium truncate">{selectedProject.name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 truncate">
                      {selectedProject.description || 'No description'}
                    </p>
                  </div>
                ) : (
                  <div className="text-xs text-muted-foreground text-center py-2">
                    No project selected
                  </div>
                )}
              </div>
            </div>

            {/* Project Manager Modal */}
            {showProjectManager && (
              <div className="border-b bg-white dark:bg-gray-800 max-h-96 overflow-y-auto">
                <div className="p-3">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold">Manage Projects</h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowProjectManager(false)}
                      className="h-6 w-6 p-0"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                  <ChatProjectManager
                    userId={1} // You'll need to get the actual user ID from context
                    onProjectSelect={(project) => {
                      setSelectedProject(project);
                      setShowProjectManager(false);
                    }}
                    selectedProjectId={selectedProject?.id}
                  />
                </div>
              </div>
            )}

            {/* Chat Sessions List */}
            {isLoadingSessions ? (
              <div className="p-4 text-center text-muted-foreground">Loading sessions...</div>
            ) : (
              <>
                {sessionsData && sessionsData.length > 0 ? (
                  <div className="p-2 space-y-4">
                    {/* Group sessions by time periods */}
                    {(() => {
                      // Helper function to check if date is within the specified period
                      const isToday = (date: Date) => {
                        const today = new Date();
                        return date.getDate() === today.getDate() &&
                          date.getMonth() === today.getMonth() &&
                          date.getFullYear() === today.getFullYear();
                      };
                      
                      const isYesterday = (date: Date) => {
                        const yesterday = new Date();
                        yesterday.setDate(yesterday.getDate() - 1);
                        return date.getDate() === yesterday.getDate() &&
                          date.getMonth() === yesterday.getMonth() &&
                          date.getFullYear() === yesterday.getFullYear();
                      };
                      
                      const isWithinDays = (date: Date, days: number) => {
                        const periodStart = new Date();
                        periodStart.setDate(periodStart.getDate() - days);
                        return date >= periodStart && !isToday(date) && !isYesterday(date);
                      };
                      
                      const isWithin7Days = (date: Date) => isWithinDays(date, 7);
                      const isWithin30Days = (date: Date) => isWithinDays(date, 30) && !isWithin7Days(date);
                      
                      const getMonthYear = (date: Date) => {
                        return `${date.toLocaleString('default', { month: 'long' })} ${date.getFullYear()}`;
                      };
                      
                      // Sort sessions by updatedAt date in descending order (newest first)
                      const sortedSessions = sessionsData
                        .slice()
                        .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
                      
                      // Group sessions by time period
                      const todaySessions = sortedSessions.filter(s => isToday(new Date(s.updatedAt)));
                      const yesterdaySessions = sortedSessions.filter(s => isYesterday(new Date(s.updatedAt)));
                      const within7DaysSessions = sortedSessions.filter(s => isWithin7Days(new Date(s.updatedAt)));
                      const within30DaysSessions = sortedSessions.filter(s => isWithin30Days(new Date(s.updatedAt)));
                      
                      // Group remaining sessions by month
                      const olderSessions = sortedSessions.filter(s => {
                        const date = new Date(s.updatedAt);
                        return !isToday(date) && !isYesterday(date) && !isWithin7Days(date) && !isWithin30Days(date);
                      });
                      
                      // Group older sessions by month and year
                      const monthGroups: Record<string, typeof olderSessions> = {};
                      olderSessions.forEach(session => {
                        const monthYear = getMonthYear(new Date(session.updatedAt));
                        if (!monthGroups[monthYear]) {
                          monthGroups[monthYear] = [];
                        }
                        monthGroups[monthYear].push(session);
                      });
                      
                      // Render session item
                      const renderSession = (session: ChatSession) => (
                        <div 
                          key={session.id}
                          className={`p-2 rounded-md flex items-center justify-between cursor-pointer hover:bg-accent/50 ${activeSessionId === session.id ? 'bg-accent' : ''}`}
                          onClick={() => setActiveSessionId(session.id)}
                        >
                          <div className="flex-1 truncate">
                            {activeSessionId === session.id ? (
                              <Input
                                value={newSessionTitle}
                                onChange={(e) => setNewSessionTitle(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    updateSessionMutation.mutate({
                                      id: session.id,
                                      updates: { title: newSessionTitle }
                                    });
                                  } else if (e.key === 'Escape') {
                                    setActiveSessionId(null);
                                  }
                                }}
                                onClick={(e) => e.stopPropagation()}
                                autoFocus
                              />
                            ) : (
                              <span className="block truncate">{session.title}</span>
                            )}
                          </div>
                          
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                              <Button variant="ghost" size="icon" aria-label={`Actions for ${session.title}`} className="h-8 w-8">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={(e) => {
                                e.stopPropagation();
                                setActiveSessionId(session.id);
                              }}>
                                <Edit className="mr-2 h-4 w-4" />
                                Rename
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                className="text-destructive"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteSessionMutation.mutate(session.id);
                                }}
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      );
                      
                      // Render group with heading
                      const renderGroup = (title: string, sessionGroup: ChatSession[]) => {
                        if (!sessionGroup.length) return null;
                        return (
                          <div key={title} className="space-y-1">
                            <h3 className="text-xs font-medium text-muted-foreground px-2 my-2">{title}</h3>
                            {sessionGroup.map(renderSession)}
                          </div>
                        );
                      };
                      
                      return (
                        <>
                          {renderGroup("Today", todaySessions)}
                          {renderGroup("Yesterday", yesterdaySessions)}
                          {renderGroup("Previous 7 Days", within7DaysSessions)}
                          {renderGroup("Previous 30 Days", within30DaysSessions)}
                          
                          {/* Render monthly groups */}
                          {Object.entries(monthGroups).map(([monthYear, sessions]) => 
                            renderGroup(monthYear, sessions)
                          )}
                        </>
                      );
                    })()}
                  </div>
                ) : (
                  <div className="p-4 text-center text-muted-foreground">
                    No chat sessions yet
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
      
      {/* Main chat area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* MAJOR CHANGE: Determine which view to show based on a boolean check of activeSessionId */}
        {activeSessionId !== null ? (
          /* Chat active view */
          <>
            {/* Chat header with model info and New Chat button */}
            {activeSessionData && activeSessionData.session && (
              <div className="p-4 border-b">
                <div className="max-w-[900px] mx-auto">
                  <div>
                    <h2 className="font-semibold">{activeSessionData.session.title}</h2>
                    <p className="text-sm text-muted-foreground">
                      {activeSessionData.session.provider} - {activeSessionData.session.model}
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Chat messages */}
            <ScrollArea className="flex-1 p-4">
              <div className="space-y-4 mx-auto max-w-[900px]">
                {/* Debug display for activeSession structure - show in dev mode */}
                {true && (
                  <div className="bg-yellow-100 dark:bg-yellow-900/20 p-2 mb-4 text-xs overflow-auto max-h-[100px]">
                    <p>Active Session ID: {activeSessionId}</p>
                    <p>Has activeSession: {activeSession ? 'Yes' : 'No'}</p>
                    <p>Has session in data: {activeSessionData?.session ? 'Yes' : 'No'}</p>
                    <p>Has messages in data: {activeMessages ? `Yes (${activeMessages.length})` : 'No'}</p>
                    {activeMessages && <p>Messages count: {activeMessages.length}</p>}
                  </div>
                )}
                
                {activeMessages && activeMessages.length > 0 ? (
                  // Display messages
                  activeMessages
                    .filter(msg => msg.role !== 'system') // Don't display system messages
                    .map(message => (
                      <div key={message.id} className={`flex items-start gap-2 ${message.role === 'user' ? 'justify-start' : 'justify-end'}`}>
                        {/* User avatar on the left for user messages */}
                        {message.role === 'user' && (
                          <div className="flex-shrink-0 h-8 w-8 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden flex items-center justify-center">
                            <svg className="h-5 w-5 text-gray-500" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z" />
                            </svg>
                          </div>
                        )}
                        
                        <div 
                          className={`max-w-[80%] p-3 rounded-lg shadow-sm ${
                            message.role === 'user' 
                              ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white dark:from-blue-600 dark:to-cyan-600 border-0' 
                              : 'bg-white dark:bg-gray-800 ai-message-border'
                          }`}
                        >
                          <div className={`whitespace-pre-wrap ${message.role === 'assistant' ? 'ai-message-content' : ''}`}>
                            {formatMessageContent(message.content)}
                            {message.role === 'assistant' && (
                              <>
                                
                                {(message as any)?.metadata?.unsplash?.images?.length > 0 && (
                                  <UnsplashGrid meta={(message as any).metadata} />
                                )}
                              </>
                            )}
                          </div>
                        </div>
                        
                        {/* AI avatar on the right for AI messages */}
                        {message.role === 'assistant' && (
                          <div className="flex-shrink-0 h-8 w-8 rounded-full bg-gradient-to-r from-purple-500 to-indigo-600 overflow-hidden flex items-center justify-center">
                            <img 
                              src="/assets/aivia-icon-new.png" 
                              alt="AIVIA" 
                              className="h-6 w-6 object-contain"
                            />
                          </div>
                        )}
                      </div>
                    ))
                ) : (
                  // Show a message if there are no messages yet
                  <div className="text-center text-muted-foreground p-4">
                    No messages yet. Start a conversation by sending a message below.
                  </div>
                )}
                
                {isWaitingForResponse && (
                  <div className="flex items-center justify-center mt-8 mb-8">
                    <div className="flex flex-col items-center">
                      <div className="blue-orb-large mb-4" />
                      <p className="matrix-text ai-oracle-loading-message">{getRandomLoadingMessage()}</p>
                    </div>
                  </div>
                )}
                
                <div ref={endOfMessagesRef} />
              </div>
            </ScrollArea>
            
            {/* Message input */}
            <div className="p-4 border-t">
              <div className="flex gap-2 max-w-[900px] mx-auto">
                <Textarea
                  placeholder={isWaitingForResponse ? "Waiting for AI response..." : "Type your message..."}
                  className="flex-1 min-h-[60px] max-h-[200px]"
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  disabled={isWaitingForResponse}
                />
                <Button 
                  className="self-end bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white border-0 shadow-sm" 
                  onClick={handleSendMessage}
                  disabled={!messageInput.trim() || isWaitingForResponse || sendMessageMutation.isPending}
                >
                  {isWaitingForResponse || sendMessageMutation.isPending ? (
                    <div className="blue-orb-small" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          </>
        ) : (
          /* Model selector view */
          <div className="flex-1 flex flex-col">
            {/* Debug info - active in dev mode */}
            {true && (
              <div className="p-2 bg-green-100 dark:bg-green-900/20 text-xs">
                <p>Model selector view active</p>
                <p>activeSessionId: {activeSessionId === null ? 'null' : activeSessionId}</p>
                <p>pendingChatConfig: {pendingChatConfig ? JSON.stringify(pendingChatConfig) : 'null'}</p>
                <p>selectedProvider: {selectedProvider || 'none'}</p>
                <p>selectedModel: {selectedModel || 'none'}</p>
              </div>
            )}
            
            {/* Model Selector Bar */}
            <div className="border-b bg-gray-50 dark:bg-gray-900 h-[70px] flex items-center px-4">
              <div className="max-w-[900px] mx-auto w-full">
                <div className="flex items-center justify-between gap-3 h-full">
                  <h3 className="text-sm font-medium whitespace-nowrap">Select AI Model:</h3>
                  <div className="flex items-center gap-3 flex-1 overflow-x-auto">
                    <Select
                      value={selectedProvider || DEFAULT_AI_PROVIDER}
                      onValueChange={(value) => {
                        setSelectedProvider(value);
                        // Set a default model for the selected provider
                        const provider = providerList.find((p) => p.provider === value);

                        if (!provider || !provider.models || provider.models.length === 0) {
                          setSelectedModel(null);
                          setPendingChatConfig(null);
                          return;
                        }

                        const defaultModel = provider.models[0];
                        setSelectedModel(defaultModel.id);
                        setPendingChatConfig({
                          provider: value,
                          model: defaultModel.id
                        });
                      }}
                    >
                      <SelectTrigger className="w-[180px] h-[50px]">
                        <SelectValue placeholder="Select provider" />
                      </SelectTrigger>
                      <SelectContent>
                        {isLoadingProviders ? (
                          <div className="p-2 text-center text-muted-foreground">Loading...</div>
                        ) : (
                          providerList.map((provider) => (
                            <SelectItem 
                              key={provider.provider} 
                              value={provider.provider}
                              disabled={!provider.available}
                            >
                              {provider.name}
                              {!provider.available && " (API key required)"}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                    
                    <Select
                      value={selectedModel || ''}
                      onValueChange={(value) => {
                        setSelectedModel(value);
                        
                        // Update pending chat config
                        if (selectedProvider) {
                          setPendingChatConfig({
                            provider: selectedProvider,
                            model: value
                          });
                        }
                      }}
                      disabled={!selectedProvider}
                    >
                      <SelectTrigger className="w-[220px] h-[50px]">
                        <SelectValue placeholder={selectedProvider ? "Select model" : "Select a provider first"} />
                      </SelectTrigger>
                      <SelectContent>
                        {getAvailableModels().map((model: ChatProvider["models"][number]) => (
                          <SelectItem 
                            key={model.id} 
                            value={model.id}
                          >
                            {model.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    {selectedProvider && selectedModel && (
                      <div className="text-xs text-muted-foreground max-w-md">
                        {getAvailableModels().find((model: ChatProvider["models"][number]) => model.id === selectedModel)?.description || ''}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Welcome content */}
            <div className="flex-1 flex flex-col items-center justify-center p-4">
              <MessageSquare className="h-12 w-12 text-muted-foreground mb-4" />
              <h2 className="text-xl font-semibold mb-2">Welcome to AIVIA</h2>
              <p className="text-center text-muted-foreground max-w-md mb-6">
                Get intelligent travel planning suggestions and assistance with your trip itineraries and client recommendations.
              </p>
              <p className="text-center text-sm text-muted-foreground max-w-md mb-8">
                Type your message below to start a new conversation with {selectedProviderDetails?.name ?? (selectedProvider ?? 'AI')}.
              </p>
            </div>
            
            {/* Message input */}
            <div className="p-4 border-t">
              <div className="flex gap-2 max-w-[900px] mx-auto">
                <Textarea
                  placeholder={isWaitingForResponse ? "Waiting for AI response..." : "Type your message to start a new chat..."}
                  className="flex-1 min-h-[60px] max-h-[200px]"
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  disabled={isWaitingForResponse || !selectedProvider || !selectedModel}
                />
                <Button 
                  className="self-end bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white border-0 shadow-sm" 
                  onClick={handleSendMessage}
                  disabled={!messageInput.trim() || isWaitingForResponse || !selectedProvider || !selectedModel}
                >
                  {isWaitingForResponse ? (
                    <div className="blue-orb-small" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
